import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

type AboutSwarmindSectionProps = {
  t: (key: any) => string;
  onGetAccess: () => void;
};

export const AboutSwarmindSection: React.FC<AboutSwarmindSectionProps> = ({ t, onGetAccess }) => {
  const [rotation, setRotation] = useState(90);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let rafId: number;
    const updateRotation = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const triggerLine = viewportHeight * 0.7;
      if (rect.bottom > triggerLine) {
        setRotation(90);
        return;
      }
      const scrollRange = 320;
      const scrolledPast = triggerLine - rect.bottom;
      const progress = Math.min(1, Math.max(0, scrolledPast) / scrollRange);
      setRotation(90 + progress * 59);
    };
    const handleScroll = () => {
      rafId = requestAnimationFrame(updateRotation);
    };

    const scrollTarget = document.scrollingElement ?? document.documentElement;
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    const intervalId = window.setInterval(handleScroll, 80);
    handleScroll();
    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.clearInterval(intervalId);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="about-swar" ref={sectionRef}>
      <svg
        className="left-triangle"
        viewBox="0 0 346 400"
        preserveAspectRatio="xMidYMid meet"
        style={{ transform: `rotate(${rotation}deg)` }}
        aria-hidden="true"
      >
        <defs>
          <filter id="triangleGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="rgba(255, 99, 132, 0.5)" />
            <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor="rgba(255, 99, 132, 0.3)" />
            <feDropShadow dx="0" dy="0" stdDeviation="15" floodColor="rgba(255, 99, 132, 0.2)" />
          </filter>
        </defs>
        <path
          d="M0 0 L346 200 L0 400 Z"
          fill="none"
          stroke="rgba(255, 99, 132, 0.5)"
          strokeWidth="1"
          filter="url(#triangleGlow)"
        />
      </svg>

      <div className="container">
        <div className="about-swar-content">
          <div className="about-swar-left">
            <h2>
              {t('aboutSwarMind')
                .split('Swarmind')
                .map((part: string, i: number, arr: string[]) =>
                  i === arr.length - 1 ? (
                    <span key={i}>{part}</span>
                  ) : (
                    <span key={i}>
                      {part}
                      <span className="accent-text">Swarmind</span>
                    </span>
                  )
                )}
            </h2>
            <p>{t('aboutSwarMindDesc1')}</p>
            <p>{t('aboutSwarMindDesc2')}</p>
            <button className="cta-button" onClick={onGetAccess} type="button">
              {t('getAccess')}
            </button>
          </div>

          <div className="about-swar-right">
            <div className="swar-card">
              <div className="swar-card-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="swar-card-content">
                <h3>{t('quickStart')}</h3>
                <p>{t('quickStartDesc')}</p>
                <Link to="/unified-api" className="learn-more-link">
                  {t('learnMore')}
                </Link>
              </div>
            </div>

            <div className="swar-card">
              <div className="swar-card-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="swar-card-content">
                <h3>{t('infrastructureSavings')}</h3>
                <p>{t('infrastructureSavingsDesc')}</p>
                <Link to="/gpu-rental" className="learn-more-link">
                  {t('learnMore')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

